import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import Home from "@/pages/Home";
import Admin from "@/pages/Admin";
import ServicePage from "@/pages/ServicePage";
import CityPage from "@/pages/CityPage";
import ContactPage from "@/pages/ContactPage";
import CitiesPage from "@/pages/CitiesPage";
import KirklandTowingPage from "@/pages/KirklandTowingPage";
import { SERVICE_SLUG_ALIASES } from "@/config/serviceContent";
import { CITY_SLUG_ALIASES } from "@/config/cities";

// Redirect old /services/:slug and /towing/:citySlug URLs to the new flat paths.
function ServiceLegacyRedirect() {
  const { slug } = useParams();
  const mapped = SERVICE_SLUG_ALIASES[slug] || slug;
  return <Navigate to={`/${mapped}`} replace />;
}
function CityLegacyRedirect() {
  const { citySlug } = useParams();
  const mapped = CITY_SLUG_ALIASES[citySlug] || citySlug;
  return <Navigate to={`/tow-truck-${mapped}`} replace />;
}

// Adapters that hand each flat route the right slug.
const withServiceSlug = (slug) => () => {
  return <ServicePage overrideSlug={slug} />;
};
const withCitySlug = (slug) => () => {
  return <CityPage overrideSlug={slug} />;
};

function App() {
  return (
    <HelmetProvider>
      <div className="App">
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Home />} />

            {/* Service pages (flat SEO URLs) */}
            <Route path="/emergency-towing" element={withServiceSlug("emergency-towing")()} />
            <Route path="/flatbed-towing" element={withServiceSlug("flatbed-towing")()} />
            <Route path="/roadside-assistance" element={withServiceSlug("roadside-assistance")()} />
            <Route path="/jump-start" element={withServiceSlug("jump-start")()} />
            <Route path="/car-lockout" element={withServiceSlug("car-lockout")()} />
            <Route path="/tire-change" element={withServiceSlug("tire-change")()} />
            <Route path="/winch-out-service" element={withServiceSlug("winch-out-service")()} />
            <Route path="/motorcycle-towing" element={withServiceSlug("motorcycle-towing")()} />
            <Route path="/equipment-transportation" element={withServiceSlug("equipment-transportation")()} />
            <Route path="/long-distance-towing" element={withServiceSlug("long-distance-towing")()} />

            {/* City pages */}
            <Route path="/tow-truck-bellevue" element={withCitySlug("bellevue")()} />
            {/* /tow-truck-kirkland is consolidated into /kirkland-towing — redirect for backward compat */}
            <Route path="/tow-truck-kirkland" element={<Navigate to="/kirkland-towing" replace />} />
            <Route path="/tow-truck-redmond" element={withCitySlug("redmond")()} />
            <Route path="/tow-truck-bothell" element={withCitySlug("bothell")()} />
            <Route path="/tow-truck-woodinville" element={withCitySlug("woodinville")()} />
            <Route path="/tow-truck-issaquah" element={withCitySlug("issaquah")()} />
            <Route path="/tow-truck-mercer-island" element={withCitySlug("mercer-island")()} />
            <Route path="/tow-truck-newcastle" element={withCitySlug("newcastle")()} />
            <Route path="/tow-truck-sammamish" element={withCitySlug("sammamish")()} />

            {/* Cities index */}
            <Route path="/cities" element={<CitiesPage />} />

            {/* Dedicated Kirkland SEO landing page — this is the single primary Kirkland URL */}
            <Route path="/kirkland-towing" element={<KirklandTowingPage />} />

            {/* Legacy redirects from previous URL structure */}
            <Route path="/services/:slug" element={<ServiceLegacyRedirect />} />
            <Route path="/towing/:citySlug" element={<CityLegacyRedirect />} />

            <Route path="/contact" element={<ContactPage />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="*" element={<Home />} />
          </Routes>
        </BrowserRouter>
      </div>
    </HelmetProvider>
  );
}

export default App;
